import useSessionStore from "@features/session/hooks/useSessionStore";
import type {
	BaseItemContent,
	BaseItemSchema,
} from "@memorang/types/src/session/new-session";
import type { IconType } from "@memorang/ui";
import { useMemo } from "react";
import type { ProethicsScenarioId } from "../types";
import { SCENARIO_REGISTRY } from "./scenario-registry";

export type ScenarioPersona = {
	name: string;
	role: string;
	description: string;
	avatarUrl?: string;
};

export type ScenarioImageSource = {
	url: string;
	title?: string;
};

export type Scenario = {
	screenTitle: string;
	// Empty-state prompt shown before the conversation starts.
	prompt: string;
	conversationGoals: string[];
	persona: ScenarioPersona;
	scenarioSummary: string[];
	scenarioMedia: ScenarioImageSource[];
	learningObjectives: string[];
};

export type ReflectSkillMeta = {
	name: string;
	icon: IconType;
	description: string;
	// Max points for this criterion; comes from the JSON rubric so the scale
	// lives in one place (currently 0-4).
	max: number;
};

export type ScenarioBundle = {
	scenarioId: ProethicsScenarioId;
	scenario: Scenario;
	// Summative skills arrive over SSE with truncated names, so result rows are
	// matched to this metadata by index, not name. The rubric criteria order
	// (preserved on the session item) defines that index order.
	reflectSkills: ReflectSkillMeta[];
};

type SimulationContent = NonNullable<BaseItemContent["simulation"]>;

const EMPTY_SIMULATION: SimulationContent = {
	screenTitle: "",
	prompt: "",
	persona: { name: "", role: "", description: "" },
	scenarioText: "",
	scenarioMedia: [],
	objectives: [],
	reflectSkills: [],
};

const buildBundle = (
	simulation: SimulationContent,
	scenarioId: ProethicsScenarioId,
): ScenarioBundle => {
	const registry = SCENARIO_REGISTRY[scenarioId];

	return {
		scenarioId,
		scenario: {
			screenTitle: simulation.screenTitle,
			prompt: simulation.prompt,
			conversationGoals: registry.conversationGoals,
			persona: simulation.persona,
			// Split into paragraphs so ScenarioSummary can slot the example image
			// after the first paragraph ("...A specific example is below:").
			scenarioSummary: simulation.scenarioText
				.split(/\n{2,}/)
				.map((paragraph) => paragraph.trim())
				.filter(Boolean),
			scenarioMedia: simulation.scenarioMedia,
			learningObjectives: simulation.objectives,
		},
		reflectSkills: simulation.reflectSkills,
	};
};

const simulationFor = (
	items: BaseItemSchema[],
	scenarioId: ProethicsScenarioId,
): SimulationContent | undefined =>
	items.find(
		(item) =>
			item.metadata?.scenarioId === scenarioId && item.content?.simulation,
	)?.content?.simulation;

const firstSimulation = (
	items: BaseItemSchema[],
): SimulationContent | undefined =>
	items.find((item) => item.content?.simulation)?.content?.simulation;

// Reads from the active session (not module JSON), so the live-API swap is
// drop-in. Falls back to any session simulation, then an empty shell, so the UI
// still renders when the exact scenario isn't loaded yet.
export const useScenarioBundle = (
	scenarioId: ProethicsScenarioId,
): ScenarioBundle => {
	const sessionItems = useSessionStore((state) => state.sessionItems);

	return useMemo(() => {
		const simulation =
			simulationFor(sessionItems, scenarioId) ??
			firstSimulation(sessionItems) ??
			EMPTY_SIMULATION;
		return buildBundle(simulation, scenarioId);
	}, [sessionItems, scenarioId]);
};
